/* eslint-disable @typescript-eslint/no-explicit-any */

// Banco local (IndexedDB) do SIAC offline — o MESMO banco (nome e estrutura)
// no PDVSIAC e no FrontSIAC: abertos pelo mesmo endereço no navegador, os
// dois leem os mesmos cadastros e a mesma fila. Guarda uma cópia dos
// cadastros usados na venda e no login (baixada pela sincronização, ver
// sincronizacao.ts) e a fila de lançamentos feitos sem internet (vendas,
// parceiros novos e abertura de caixa) que ainda precisam ser enviados.
//
// IndexedDB em vez de localStorage: o catálogo de produtos pode ter dezenas
// de milhares de linhas, bem acima do limite de ~5MB do localStorage.

const NOME_BANCO = 'siac-offline';
const VERSAO_BANCO = 1;

export type TStore =
  | 'produtos'
  | 'referencias'
  | 'parceiros'
  | 'planocontas'
  | 'operacoes'
  | 'contas'
  | 'usuarios'
  | 'vendedores'
  | 'kv'
  | 'fila';

const CHAVES: Record<TStore, string> = {
  produtos: 'idproduto',
  referencias: 'idref',
  parceiros: 'idparceiro',
  planocontas: 'idplanoconta',
  operacoes: 'idoperacoes',
  contas: 'idconta',
  usuarios: 'iduser',
  vendedores: 'iduser',
  kv: 'chave',
  fila: 'id',
};

let conexaoBanco: Promise<IDBDatabase> | null = null;

const abrirBanco = (): Promise<IDBDatabase> => {
  if (conexaoBanco) return conexaoBanco;
  conexaoBanco = new Promise((resolve, reject) => {
    const requisicao = indexedDB.open(NOME_BANCO, VERSAO_BANCO);
    requisicao.onupgradeneeded = () => {
      const banco = requisicao.result;
      (Object.keys(CHAVES) as TStore[]).forEach((store) => {
        if (!banco.objectStoreNames.contains(store)) {
          banco.createObjectStore(store, { keyPath: CHAVES[store] });
        }
      });
    };
    requisicao.onsuccess = () => resolve(requisicao.result);
    requisicao.onerror = () => {
      conexaoBanco = null;
      reject(requisicao.error);
    };
  });
  return conexaoBanco;
};

const concluir = (transacao: IDBTransaction): Promise<void> => new Promise((resolve, reject) => {
  transacao.oncomplete = () => resolve();
  transacao.onerror = () => reject(transacao.error);
  transacao.onabort = () => reject(transacao.error);
});

const resultado = <T>(requisicao: IDBRequest<T>): Promise<T> => new Promise((resolve, reject) => {
  requisicao.onsuccess = () => resolve(requisicao.result);
  requisicao.onerror = () => reject(requisicao.error);
});

export const dbGetAll = async <T = any>(store: TStore): Promise<T[]> => {
  const banco = await abrirBanco();
  return resultado(banco.transaction(store, 'readonly').objectStore(store).getAll()) as Promise<T[]>;
};

export const dbGet = async <T = any>(store: TStore, chave: string): Promise<T | undefined> => {
  const banco = await abrirBanco();
  return resultado(banco.transaction(store, 'readonly').objectStore(store).get(chave)) as Promise<T | undefined>;
};

export const dbPut = async (store: TStore, valor: any): Promise<void> => {
  const banco = await abrirBanco();
  const transacao = banco.transaction(store, 'readwrite');
  transacao.objectStore(store).put(valor);
  return concluir(transacao);
};

export const dbDelete = async (store: TStore, chave: string): Promise<void> => {
  const banco = await abrirBanco();
  const transacao = banco.transaction(store, 'readwrite');
  transacao.objectStore(store).delete(chave);
  return concluir(transacao);
};

export const dbCount = async (store: TStore): Promise<number> => {
  const banco = await abrirBanco();
  return resultado(banco.transaction(store, 'readonly').objectStore(store).count());
};

// Troca o conteúdo inteiro de uma store numa transação só — se algo falhar
// no meio, fica a cópia anterior inteira (nunca meio catálogo).
export const dbReplaceAll = async (store: TStore, valores: any[]): Promise<void> => {
  const banco = await abrirBanco();
  const transacao = banco.transaction(store, 'readwrite');
  const objectStore = transacao.objectStore(store);
  objectStore.clear();
  valores.forEach((valor) => objectStore.put(valor));
  return concluir(transacao);
};

// Grava só os registros passados, SEM apagar o resto da store — usado pela
// sincronização incremental (ver sincronizacao.ts): o servidor manda só quem
// mudou desde a última vez, e o que não mudou continua intacto localmente.
export const dbUpsertMany = async (store: TStore, valores: any[]): Promise<void> => {
  if (!valores.length) return;
  const banco = await abrirBanco();
  const transacao = banco.transaction(store, 'readwrite');
  const objectStore = transacao.objectStore(store);
  valores.forEach((valor) => objectStore.put(valor));
  return concluir(transacao);
};

// Atalhos pra store chave/valor (configuração, empresa, sessão, status...).
export const kvGet = async <T = any>(chave: string): Promise<T | undefined> => {
  try {
    const registro = await dbGet<{ chave: string; valor: T }>('kv', chave);
    return registro?.valor;
  } catch {
    return undefined;
  }
};

export const kvSet = async (chave: string, valor: any): Promise<void> => {
  try {
    await dbPut('kv', { chave, valor });
  } catch {
    // Cache é best-effort — nunca derruba o fluxo online por causa dele.
  }
};

export const kvDelete = async (chave: string): Promise<void> => {
  try {
    await dbDelete('kv', chave);
  } catch {
    // idem kvSet
  }
};
